import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

// const api = axios.create({
//     baseURL: baseUrl,
//     paramsSerializer: (params) => {
//         return Object.entries(params)
//             .map(([key, value]) => {
//                 if (value === null || value === undefined) {
//                     return `${encodeURIComponent(key)}=null`;
//                 }
//                 if (value === "") {
//                     return `${encodeURIComponent(key)}=`;
//                 }
//                 return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
//             })
//             .join("&");
//     },
// });


const AccountMaster = () => {
    const [accounts, setAccounts] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());


    const [filter, setFilter] = useState({
        acc_code: "",
        name_on_account: "",
        plbs_group_codeid: "",
        accountnature_id: "",
        accounttype_id: "",
    });

    const handleShowFilterModal = () => setShowFilterModal(true);
    const handleCloseFilterModal = () => setShowFilterModal(false);


    const fetchAccounts = async () => {
        try {
            const response = await axios.get(baseUrl+"/searchaccountmaster", {
                params: {
                    acc_code: "",
                    name_on_account: "",
                    plbs_group_codeid: null,
                    accountnature_id: null,
                    accounttype_id: null,
                },
            });

            setAccounts(response.data || []);
        } catch (error) {
            console.error("Error fetching accounts:", error);
             setAccounts([]);

        }
    };

    const handleFilterSearch = async (e) => {
        e.preventDefault();

        const params = {
            _account_master_id: null,
            _acc_code: filter.acc_code ?? "",
            _name_on_account: filter.name_on_account ?? "",
            _plbs_group_codeid:
                filter.plbs_group_codeid === "" ? null : filter.plbs_group_codeid,
            _accountnature_id:
                filter.accountnature_id === "" ? null : filter.accountnature_id,
            _accounttype_id:
                filter.accounttype_id === "" ? null : filter.accounttype_id,
        };

        try {
            const response = await axios.get(baseUrl+"/searchaccountmaster", { params });
            setAccounts(response.data || []);
            handleCloseFilterModal();
        } catch (error) {
            setAccounts([]);
         
          

        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setFilter({
            acc_code: "",
            name_on_account: "",
            plbs_group_codeid: "",
            accountnature_id: "",
            accounttype_id: "",
        });
        setShowFilterModal(true);
        fetchAccounts();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (account_master_id) => {
        const selected = accounts.find((a) => a.account_master_id === account_master_id);
        if (selected) {
            navigate("/finance/accounts/add", {
                state: {
                    accountData: selected,          // existing account row data
                    account_master_id,              // explicitly pass ID (optional, but clear)
                    mode: "edit",
                }
            });


        } else {
            console.error(`Account Master ID ${account_master_id} not found`);
        }
    };

    const handleDeleteClick = async (account_master_id) => {
        if (!window.confirm("Are you sure you want to inactivate this account?")) return;

        try {
            await axios.post(baseUrl+`/accountmasterdelete/${account_master_id}`);
            toast.success("Account inactivated");
            fetchAccounts(); // reload grid
        } catch (error) {
            console.error("Error inactivating account:", error);
            toast.error("Error inactivating account");
        }
    };



    const searchableColumns = [
        (row) => row.acc_code,
        (row) => row.name_on_account,
        (row) => row.accounttype_description,
        (row) => row.accountnature_description,
        (row) => row.group_accountname,
        (row) => row.plbs_group_codedesc,
        (row) => row.activestatus,
    ];

    const filteredRecords = (accounts || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || "")
                .toUpperCase()
                .replace(/[-\s]+/g, "");
            const normalizedQuery = searchQuery
                .toUpperCase()
                .replace(/[-\s]+/g, "");
            return stringValue.includes(normalizedQuery);
        })
    );

    const columns = [
        {
            field: "acc_code",
            headerName: "Account Code",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "name_on_account",
            headerName: "Account Name",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "group_accountname",
            headerName: "Role Types",
            flex: 1.5,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "accountnature_description",
            headerName: "Account Nature",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "accounttype_description",
            headerName: "Account Type",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "voucher_prefix",
            headerName: "Voucher Prefix",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "opening_balance",
            headerName: "Opening Balance",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "plbs_group_codedesc",
            headerName: "P&L/BS Group Code",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "activestatus",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        ...(canSubmit
            ? [
                {
                    field: "actions",
                    headerName: "Actions",
                    flex: 1,
                    sortable: false,
                    renderCell: (params) =>
                        params.row.account_master_id ? (
                            <div className="tableActions">
                                <Tooltip title="Edit" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleEditClick(params.row.account_master_id)}
                                    >
                                        <MdEdit />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleDeleteClick(params.row.account_master_id)}
                                    >
                                        <MdDelete />
                                    </span>
                                </Tooltip>
                            </div>
                        ) : null,
                },
            ]
            : []),
    ];

    useEffect(() => {
        setIsLoading(true);
        fetchAccounts().finally(() => setIsLoading(false));

    }, []);

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Account Master</h6>
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
                        >
                            <Button
                                className="secondaryBtn"
                                variant="secondary"
                                onClick={handleShowFilterModal}
                            >
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        {canSubmit && (
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-top">Add</Tooltip>}
                            >
                                <Button
                                    className="primaryBtn"
                                    variant="primary"
                                    onClick={() => navigate("/finance/accounts/add")}
                                >
                                    <MdAddCircle />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>
            </div>

            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={filteredRecords}
                            columns={columns}
                            getRowId={(r) => r.account_master_id}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={500}
                        />
                    )}
                </div>
            </div>


            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="acc_code">
                                    <Form.Label>Account Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filter.acc_code}
                                        placeholder="Enter Account Code"
                                        onChange={(e) =>
                                            setFilter((prev) => ({ ...prev, acc_code: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="name_on_account">
                                    <Form.Label>Account Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={filter.name_on_account}
                                        placeholder="Enter Account Name"
                                        onChange={(e) =>
                                            setFilter((prev) => ({ ...prev, name_on_account: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="plbs_group_codeid">
                                    <Form.Label>P&L/BS Group Code Id</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={filter.plbs_group_codeid}
                                        placeholder="Enter Group Code Id"
                                        onChange={(e) =>
                                            setFilter((prev) => ({ ...prev, plbs_group_codeid: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="accountnature_id">
                                    <Form.Label>Account Nature Id</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={filter.accountnature_id}
                                        placeholder="Enter Account Nature Id"
                                        onChange={(e) =>
                                            setFilter((prev) => ({ ...prev, accountnature_id: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="accounttype_id">
                                    <Form.Label>Account Type Id</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={filter.accounttype_id}
                                        placeholder="Enter Account Type Id"
                                        onChange={(e) =>
                                            setFilter((prev) => ({ ...prev, accounttype_id: e.target.value }))
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button
                        variant="secondary"
                        className="btn-info clearBtn me-2"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                    <div>
                        <Button
                            variant="secondary"
                            className="btn-danger secondaryBtn me-2"
                            onClick={handleCloseFilterModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="btn-success primaryBtn"
                            onClick={handleFilterSearch}
                        >
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AccountMaster;
